// Shared constants across the DOOH platform
import { Orientation } from './types';
/** Maximum file size for asset uploads (500MB) */
export const MAX_ASSET_FILE_SIZE = 500 * 1024 * 1024;
/** Supported image MIME types */
export const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Supported video MIME types */
export const SUPPORTED_VIDEO_TYPES = ['video/mp4'];
/** All supported asset MIME types */
export const SUPPORTED_ASSET_TYPES = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_VIDEO_TYPES];
/** Default duration for image assets in playlists (seconds) */
export const DEFAULT_IMAGE_DURATION = 10;
/** Maximum number of items in a playlist */
export const MAX_PLAYLIST_ITEMS = 100;
/** Heartbeat interval in seconds */
export const HEARTBEAT_INTERVAL_SECONDS = 60;
/** Screen offline threshold in seconds */
export const OFFLINE_THRESHOLD_SECONDS = 300; // 5 minutes
/** Pairing code expiry in seconds */
export const PAIRING_CODE_EXPIRY_SECONDS = 600; // 10 minutes
/** Pairing poll rate limit in seconds */
export const PAIRING_POLL_RATE_LIMIT_SECONDS = 5;
/** Pairing user code length (human-readable short code) */
export const PAIRING_USER_CODE_LENGTH = 6;
/** Pairing device code length (longer code for API polling) */
export const PAIRING_DEVICE_CODE_LENGTH = 32;
/** Verification URI base path */
export const PAIRING_VERIFY_PATH = '/pairing/verify';
/** WSS URL refresh interval in hours */
export const WSS_URL_REFRESH_HOURS = 12;
/** WSS URL expiry duration in hours */
export const WSS_URL_EXPIRY_HOURS = 24;
/** Rate limit for WS URL requests: minimum seconds between requests per device */
export const WS_URL_RATE_LIMIT_SECONDS = 60;
/**
 * Manifest validity horizon (#GEN-127). The manifest's validUntil tracks the
 * actual schedule horizon (how long the compiled schedule stays relevant)
 * rather than a fixed 24h window that forced a daily server-side recompile.
 * It is capped at MANIFEST_MAX_TTL_DAYS so embedded presigned asset URLs are
 * still refreshed periodically and a stale snapshot can't linger forever, and
 * floored at MANIFEST_MIN_TTL_DAYS so short/undated schedules still get a
 * comfortable offline window.
 */
export const MANIFEST_MAX_TTL_DAYS = 30;
export const MANIFEST_MIN_TTL_DAYS = 1;
/** Default screen resolution */
export const DEFAULT_RESOLUTION = '800x1280';
/** Default screen timezone */
export const DEFAULT_TIMEZONE = 'America/New_York';
/** Default screen orientation */
export const DEFAULT_ORIENTATION = Orientation.PORTRAIT;
/** MQTT topic prefix */
export const MQTT_TOPIC_PREFIX = 'dooh';
/**
 * Safe charset for MQTT topic components — prevents topic injection (#13).
 * Rejects MQTT wildcards (+, #) and path separators (/).
 */
const TOPIC_COMPONENT_REGEX = /^[A-Za-z0-9_-]+$/;
/** Build MQTT topic for a device */
export function buildMqttTopic(orgId, deviceId, suffix) {
    // Defensive guard: reject MQTT-significant characters that could cross
    // namespaces (#13). Zod validation catches this at input, but this ensures
    // safety even if buildMqttTopic is called with unvalidated values.
    if (!TOPIC_COMPONENT_REGEX.test(orgId)) {
        throw new Error(`Invalid orgId for MQTT topic: contains forbidden characters`);
    }
    if (!TOPIC_COMPONENT_REGEX.test(deviceId)) {
        throw new Error(`Invalid deviceId for MQTT topic: contains forbidden characters`);
    }
    return `${MQTT_TOPIC_PREFIX}/${orgId}/${deviceId}/${suffix}`;
}
//# sourceMappingURL=constants.js.map